/**
 * Splits an assistant reply into markdown text segments and the charts and tables that its `[chart:<id>]` and
 * `[table:<id>]`
 * markers place. A marker with no item renders as nothing, because the item can still be streaming in. A repeated
 * marker renders
 * its item once, and an item with no marker renders after the text, charts first.
 */

import { AssistantChart, AssistantTable } from './api/assistant.service';
import { renderAssistantMarkdown } from './assistant-markdown.util';

export type AssistantMessageSegment =
    | { kind: 'text'; html: string }
    | { kind: 'chart'; chart: AssistantChart }
    | { kind: 'table'; table: AssistantTable };

const MARKER = /\[(chart|table):([\w-]+)\]/g;

export function segmentAssistantMessage(
    text: string,
    charts: readonly AssistantChart[] | undefined,
    tables: readonly AssistantTable[] = []
): AssistantMessageSegment[] {
    const chartsById = new Map((charts ?? []).filter((chart) => chart.id).map((chart) => [chart.id, chart]));
    const tablesById = new Map(tables.map((table) => [table.id, table]));
    const placed = new Set<string>();
    const segments: AssistantMessageSegment[] = [];

    const pushText = (part: string): void => {
        if (part.trim()) {
            segments.push({ kind: 'text', html: renderAssistantMarkdown(part) });
        }
    };

    let cursor = 0;
    for (const match of text.matchAll(MARKER)) {
        const [marker, type, id] = match;
        // Text up to the marker always flushes — the marker itself never renders,
        // whether it resolved to an item, was a duplicate, or matched nothing.
        pushText(text.slice(cursor, match.index));
        cursor = match.index + marker.length;
        if (placed.has(marker)) {
            continue;
        }
        const chart = type === 'chart' ? chartsById.get(id) : undefined;
        const table = type === 'table' ? tablesById.get(id) : undefined;
        if (chart) {
            placed.add(marker);
            segments.push({ kind: 'chart', chart });
        } else if (table) {
            placed.add(marker);
            segments.push({ kind: 'table', table });
        }
    }
    pushText(text.slice(cursor));

    for (const chart of charts ?? []) {
        if (!chart.id || !placed.has(`[chart:${chart.id}]`)) {
            segments.push({ kind: 'chart', chart });
        }
    }
    for (const table of tables) {
        if (!placed.has(`[table:${table.id}]`)) {
            segments.push({ kind: 'table', table });
        }
    }
    return segments;
}
